import appConstants from "../../../../appConstants";
import {
  minutesToTime,
  roundingNumbers,
  mergeUserConfig,
  suppressEnter,
} from "../../../dataGrid/gridUtilities";
import { routeNameCellStyling } from "../../../dataGrid/cellStyling";
import {
  MultiSelectFilter,
  DateTimeFilter,
  StaticFilter,
} from "../../../dataGrid/columnFilters";
import {
  TextFieldEditor,
  DateTimeEditor,
  DropdownEditor,
} from "../../../dataGrid/cellEditors";
import {
  LockStatusRenderer,
  DotRenderer,
  DropdownValueCellRenderer,
} from "../../../dataGrid/cellRenderer";
import ReturnToTerminalIcon from "../Common/ReturnToTerminalIcon";
import { DropYardMarker } from "../../../mapComponents/Markers";
import { getClientSpecificOrderedColumns } from "./RouteGridColumnOrderConfig";
import { COLUMN_CONFIG } from "../ShipmentsView/constant";
import StyledToolTip from "../../../common/StyledToolTip";

export default class GridConfig {
  constructor(configuration) {
    const config = configuration || {};
    this.name = "GridConfiguration";
    this.config = GridConfig.getConfiguration(config);
  }
  static getConfiguration({
    userConfig,
    featureFlags,
    ROUTENAME_EDITOR_PARAMS,
    columnConfig,
  }) {
    const defaultConfig = GridConfig.getDefaultConfiguration({
      featureFlags,
      ROUTENAME_EDITOR_PARAMS,
      columnConfig,
    });
    return mergeUserConfig({
      defaultConfig,
      userConfig,
    });
  }

  static isNotUnrtdRoute(params) {
    return params.data.routeName != appConstants.UNRTD_ROUTENAME;
  }
  static isNotUnrtdOrTrapRoute(params) {
    return (
      params.data.routeName != appConstants.UNRTD_ROUTENAME &&
      !params.data.isTrapRoute
    );
  }

  static getRouteCellClass(params) {
    const classes = ["gridCell-leftAlign", "routeNameCell"];
    if (params.value === appConstants.UNRTD_ROUTENAME) {
      classes.push("clickable-cell");
    }
    return classes;
  }

  static getRouteType(params) {
    if (params.data.routeName === appConstants.UNRTD_ROUTENAME) {
      return "";
    }
    if (params.data.isTrapRoute) {
      return appConstants.ROUTE_TYPES.TRAP;
    }
    return appConstants.ROUTE_TYPES.CITY;
  }

  static setRouteType(params) {
    if (params.data.isTrapRoute === params.newValue.value) {
      return false;
    }
    params.data.isTrapRoute = params.newValue.value;
    return true;
  }

  static routeModeRenderer(params) {
    if (params.data.isReturnToTerminal) {
      return <ReturnToTerminalIcon width={28} height={28} />;
    }
    return null;
  }

  static routeModeTooltipRenderer(params) {
    if (params.data.isReturnToTerminal) {
      return "Looped Route: Route created to get driver back to terminal (No pickups)";
    }
    return null;
  }

  static showDropYard({ data }) {
    if (data.dropYardId) {
      return <StyledToolTip title="Drop Yard Route"><span><DropYardMarker size= "18"/></span></StyledToolTip>;
    }
    return null;
  }

  static getDefaultConfiguration({
    featureFlags,
    ROUTENAME_EDITOR_PARAMS,
    columnConfig,
  }) {
    const columnsToHide = [];
    if (featureFlags.DisableReturnToTerminalOption) {
      columnsToHide.push("isReturnToTerminal");
    }
    if (!featureFlags.EnableDoorForRouteMapping) {
      columnsToHide.push("door");
    }
    if (columnConfig === COLUMN_CONFIG.FFE) {
      columnsToHide.push("driverId");
    } else {
      columnsToHide.push("dropYardName");
    }
    const defaultColumnOrder = [
      {
        headerName: "",
        minWidth: appConstants.columnWidth.VIOLATION_DOT,
        maxWidth: appConstants.columnWidth.VIOLATION_DOT,
        field: "violation",
        pinned: "left",
        lockPosition: true,
        cellClass: ["gridCell-centerAlign", "gridCell-dot"],
        filter: false,
        cellRendererFramework: DotRenderer,
        isInvisibleInShowHide: true,
        gridTitle: appConstants.gridNames.RoutesGrid,
        colId: "violation",
      },
      {
        headerName: " ", // keep this extra space to make it unique with other column 
        minWidth: appConstants.columnWidth.VIOLATION_DOT,
        pinned: "left",
        lockPosition: true,
        filter: false,
        maxWidth: appConstants.columnWidth.VIOLATION_DOT,
        cellClass:["gridCell-centerAlign", "gridCell-dot"],
        cellRendererFramework: GridConfig.showDropYard,
        colId: "dropYard",
      },
      {
        headerName: "Route",
        headerTooltip: "Route Name",
        headerClass: "routeNameCell",
        field: "routeName",
        minWidth: appConstants.columnWidth.ROUTES,
        editable: GridConfig.isNotUnrtdRoute,
        pinned: "left",
        lockPosition: true,
        cellClass: GridConfig.getRouteCellClass,
        cellStyle: routeNameCellStyling,
        filterFramework: MultiSelectFilter,
        cellEditorParams: {
          ...ROUTENAME_EDITOR_PARAMS,
        },
        colId: "routeName",
      },
      {
        headerName: "Mode",
        headerTooltip: "Mode",
        minWidth: appConstants.columnWidth.ROUTE_MODE,
        field: "isReturnToTerminal",
        pinned: "left",
        lockPosition: true,
        cellClass: "gridCell-leftAlign",
        cellStyle: {
          left: 100,
        },
        filter: false,
        hide: true,
        cellRendererFramework: GridConfig.routeModeRenderer,
        tooltipValueGetter: GridConfig.routeModeTooltipRenderer,
        colId: "isReturnToTerminal",
      },
      {
        headerName: "Status",
        field: "status",
        minWidth: appConstants.columnWidth.LOCK_STATUS,
        pinned: "left",
        lockPosition: true,
        headerTooltip: "Lock Status",
        cellClass: "gridCell-centerAlign",
        cellRendererFramework: LockStatusRenderer,
        filterFramework: StaticFilter,
        filterParams: {
          staticOptions: Object.values(appConstants.routeLockStates),
        },
        editable: GridConfig.isNotUnrtdRoute,
        cellEditorFramework: DropdownEditor,
        type: "isCancelBeforeStart",
        colId: "status",
      },
      {
        headerName: "Route Type",
        field: "isTrapRoute",
        minWidth: appConstants.columnWidth.TOTAL_STOPS,
        headerTooltip: "Route Type",
        filterFramework: StaticFilter,
        filterParams: {
          staticOptions: appConstants.ROUTE_TYPES_LIST,
        },
        valueFormatter: GridConfig.getRouteType,
        valueSetter: GridConfig.setRouteType,
        filterValueGetter: GridConfig.getRouteType,
        editable: GridConfig.isNotUnrtdRoute,
        cellEditorFramework: DropdownEditor,
        cellEditorParams: {
          isClearable: false,
          useFormatter: true,
        },
        colId: "isTrapRoute",
      },
      {
        headerName: "Drop Yard",
        field: "dropYardName",
        minWidth: appConstants.columnWidth.ANCHORPOINT_NAME,
        headerTooltip: "Drop Yard",
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        tooltipField: "dropYardName",
        colId: "dropYardName",
      },
      {
        headerName: "Ttl Stops",
        field: "deliveryStopsCount",
        minWidth: appConstants.columnWidth.TOTAL_STOPS,
        cellClass: "gridCell-leftAlign",
        headerTooltip: "Total Stops",
        filterFramework: MultiSelectFilter,
        valueFormatter: (params) =>
          params.data.isTrapRoute ? "" : params.value,
        colId: "deliveryStopsCount",
      },
      {
        headerName: "Ttl Stops (P)",
        field: "totalPickupStops",
        minWidth: appConstants.columnWidth.W_95,
        headerTooltip: "Ttl Stops (P)",
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        colId: "totalPickupStops",
      },
      {
        headerName: "Ttl Bills",
        headerTooltip: "Total Bills",
        field: "prosCount",
        minWidth: appConstants.columnWidth.TOTAL_BILLS,
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        colId: "prosCount",
      },
      {
        headerName: "Ttl Bills (P)",
        field: "totalPickupBills",
        minWidth: appConstants.columnWidth.W_95,
        headerTooltip: "Total Bills (P)",
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        colId: "totalPickupBills",
      },
      {
        headerName: "PSE",
        field: "totalPse",
        minWidth: appConstants.columnWidth.PSE,
        //editable: GridConfig.isEditable,
        headerTooltip: "Pallet Space Equivalent",
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        //tooltipField: "totalPse"
        hide: featureFlags.IsWeightBasedRoutingEnabled,
        colId: "totalPse",
      },
      {
        headerName: "PSE (P)",
        field: "totalPickupPallets",
        minWidth: appConstants.columnWidth.PSE,
        headerTooltip: "Pallet Space Equivalent (P)",
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        colId: "totalPickupPallets",
      },
      {
        headerName: "Calculated PSE",
        field: "totalCalculatedPse",
        minWidth: appConstants.columnWidth.CALCULATED_PSE,
        headerTooltip: "Calculated Pallet Space Equivalent",
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        hide: true,
        colId: "totalCalculatedPse",
      },
      {
        headerName: "Frozen Pallets",
        field: "frozenDeliveryPallets",
        minWidth: appConstants.columnWidth.CALCULATED_PSE,
        headerTooltip: "Frozen Pallets",
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        colId: "frozenDeliveryPallets",
      },
      {
        headerName: "Non-Frozen Pallets",
        field: "nonFrozenDeliveryPallets",
        minWidth: appConstants.columnWidth.CALCULATED_PSE + 20,
        headerTooltip: "Non-Frozen Pallets",
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        colId: "nonFrozenDeliveryPallets",
      },
      {
        headerName: "Frozen Pallets (P)",
        field: "frozenPickupPallets",
        minWidth: appConstants.columnWidth.W_140,
        headerTooltip: "Frozen Pallets (P)",
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        colId: "frozenPickupPallets",
      },
      {
        headerName: "Non-Frozen Pallets (P)",
        field: "nonFrozenPickupPallets",
        minWidth: appConstants.columnWidth.W_140,
        headerTooltip: "Non-Frozen Pallets (P)",
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        colId: "nonFrozenPickupPallets",
      },
      {
        headerName: "Weight",
        headerTooltip: "Weight",
        field: "totalWeight",
        minWidth: appConstants.columnWidth.WEIGHT,
        cellClass: "gridCell-centerAlign",
        filterFramework: MultiSelectFilter,
        colId: "totalWeight",
      },
      {
        headerName: "Weight (P)",
        field: "totalPickupWeight",
        minWidth: appConstants.columnWidth.WEIGHT,
        headerTooltip: "Weight (P)",
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        colId: "totalPickupWeight",
      },
      {
        headerName: "LG",
        field: "lgProCount",
        minWidth: appConstants.columnWidth.LG,
        headerTooltip: "Total LG pro count",
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        hide: true,
        colId: "lgProCount",
      },
      {
        headerName: "APT",
        field: "aptProCount",
        minWidth: appConstants.columnWidth.APT,
        headerTooltip: "Total APT pro count",
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        colId: "aptProCount",
      },
      {
        headerName: "Start Time",
        field: "startTime",
        minWidth: appConstants.columnWidth.START_TIME,
        headerTooltip: "Start Time",
        cellClass: "gridCell-leftAlign",
        // editable: GridConfig.isNotUnrtdOrTrapRoute,
        cellEditorFramework: DateTimeEditor,
        cellEditorParams: {
          DATETYPE: appConstants.DATETYPES.DATETIME,
        },
        filterFramework: DateTimeFilter,
        filterParams: {
          DATETYPE: appConstants.DATETYPES.DATETIME,
        },
        colId: "startTime",
      },
      {
        headerName: "Driver",
        field: "driverId",
        minWidth: appConstants.columnWidth.DRIVER_NAME,
        // editable: GridConfig.isEditable,
        headerTooltip: "Driver",
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        colId: "driverId",
      },
      {
        headerName: "Tractor",
        field: "tractor",
        headerTooltip: "Tractor",
        minWidth: appConstants.columnWidth.TRAILER_TYPE,
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        colId: "tractor",
      },
      {
        headerName: "Trailer Type",
        field: "trailerName",
        headerTooltip: "Trailer Type",
        editable: GridConfig.isNotUnrtdOrTrapRoute,
        minWidth: appConstants.columnWidth.TRAILER_TYPE,
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        cellEditorFramework: DropdownEditor,
        cellRenderer: DropdownValueCellRenderer,
        suppressKeyboardEvent: suppressEnter,
        colId: "trailerName",
      },
      {
        headerName: "Loaded Bills",
        headerTooltip: "Loaded Bills",
        field: "loadedBills",
        minWidth: appConstants.columnWidth.LOADED_BILLS,
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        valueFormatter: (params) =>
          params.data.isTrapRoute ? "" : params.value,
        hide: true,
        colId: "loadedBills",
      },
      {
        headerName: "HU",
        headerTooltip: "Handling Unit",
        field: "totalHu",
        minWidth: appConstants.columnWidth.HU,
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        colId: "totalHu",
      },
      {
        headerName: "HU (P)",
        headerTooltip: "Handling Unit (P)",
        field: "totalPickupHu",
        minWidth: appConstants.columnWidth.HU,
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        colId: "totalPickupHu",
      },
      {
        headerName: "Cubic Feet",
        headerTooltip: "Cubic Feet",
        field: "cubesCount",
        minWidth: appConstants.columnWidth.CUBIC_FEET,
        cellClass: "gridCell-centerAlign",
        filterFramework: MultiSelectFilter,
        hide: true,
        colId: "cubesCount",
      },
      // {
      //   headerName: "Load Close Time",
      //   field: "loadCloseTime",
      //   minWidth: appConstants.columnWidth.LOADCLOSE_TIME,
      //   headerTooltip: "Load Close Time",
      //   // editable: GridConfig.isEditable,
      //   valueSetter: setTimeFromDateTime,
      //   cellClass: "gridCell-leftAlign",
      //   cellEditorFramework: DateTimeEditor,
      //   cellEditorParams: {
      //     DATETYPE: appConstants.DATETYPES.TIME,
      //   },
      //   filterFramework: DateTimeFilter,
      //   filterParams: {
      //     DATETYPE: appConstants.DATETYPES.TIME,
      //   },
      // },
      {
        headerName: "Ttl Miles",
        field: "totalMiles",
        minWidth: appConstants.columnWidth.TOTAL_MILES,
        cellClass: "gridCell-leftAlign",
        headerTooltip: "Total Miles",
        valueFormatter: roundingNumbers,
        filterFramework: MultiSelectFilter,
        colId: "totalMiles",
      },
      {
        headerName: "Ttl Time",
        field: "totalTime",
        minWidth: appConstants.columnWidth.TOTAL_TIME,
        headerTooltip: "Total Time",
        cellClass: "gridCell-leftAlign",
        valueGetter: (params) =>
          minutesToTime({ value: params.data.totalTime }),
        filterFramework: MultiSelectFilter,
        colId: "totalTime",
      },
      {
        headerName: "Driver Return Time",
        field: "driverReturnTime",
        minWidth: appConstants.columnWidth.EMPTY_TIME + 40,
        headerTooltip: "Driver Return Time",
        cellClass: "gridCell-leftAlign",
        filterFramework: DateTimeFilter,
        filterParams: {
          DATETYPE: appConstants.DATETYPES.DATETIME,
        },
        colId: "driverReturnTime",
      },
      // {
      //   headerName: "Empty Time",
      //   field: "emptyTime",
      //   minWidth: appConstants.columnWidth.EMPTY_TIME,
      //   headerTooltip: "Start time + total time",
      //   cellClass: "gridCell-leftAlign",
      //   filterFramework: DateTimeFilter,
      //   filterParams: {
      //     DATETYPE: appConstants.DATETYPES.TIME,
      //   },
      // },
      {
        headerName: "Priority",
        field: "priorityProCount",
        minWidth: appConstants.columnWidth.PRIORITY,
        headerTooltip: "Total GSS pro count",
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        hide: true,
        colId: "priorityProCount",
      },
      {
        headerName: "Spl Needs",
        field: "specialNeedsProCount",
        minWidth: appConstants.columnWidth.SPL_NEED,
        headerTooltip: "Total HZ, FZ, FD pros",
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        hide: true,
        colId: "specialNeedsProCount",
      },
      {
        headerName: "Anchor Point",
        field: "anchorPointName",
        minWidth: appConstants.columnWidth.ANCHORPOINT_NAME,
        headerTooltip: "Anchor Point",
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        tooltipField: "anchorPointName",
        editable: GridConfig.isNotUnrtdOrTrapRoute,
        cellEditorFramework: DropdownEditor,
        cellRenderer: DropdownValueCellRenderer,
        suppressKeyboardEvent: suppressEnter,
        hide: true,
        colId: "anchorPointName",
      },
      {
        headerName: "Missed Apts",
        field: "numServiceViolations",
        minWidth: appConstants.columnWidth.APPOINTMENT,
        headerTooltip: "Missed Apts",
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        valueFormatter: (params) =>
          params.data.isTrapRoute ? "" : params.value,
        hide: true,
        colId: "numServiceViolations",
      },
      {
        headerName: "Comments",
        field: "comment",
        minWidth: appConstants.columnWidth.COMMENTS,
        editable: GridConfig.isNotUnrtdRoute,
        headerTooltip: "Comments",
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        tooltipField: "comment",
        colId: "comment",
      },
      {
        headerName: "AP EmpTime",
        field: "anchorPointEmptyTime",
        minWidth: appConstants.columnWidth.EMPTY_TIME,
        headerTooltip: "Anchor Point Empty Time",
        cellClass: "gridCell-leftAlign",
        filterFramework: DateTimeFilter,
        filterParams: {
          DATETYPE: appConstants.DATETYPES.TIME,
        },
        hide: true,
        colId: "anchorPointEmptyTime",
      },
      {
        headerName: "Door #",
        field: "door",
        minWidth: appConstants.columnWidth.W_100,
        headerTooltip: "Door Number",
        cellClass: "gridCell-leftAlign",
        filterFramework: MultiSelectFilter,
        colId: "door",
      },
    ];
    const defaultConfig = {
      title: appConstants.gridNames.RoutesGrid,
      keyColumn: "routeName",
      rowSelection: "multiple",
      rowDeselection: true,
      defaultColDef: {
        cellEditorFramework: TextFieldEditor,
        lockPinned: true,
        sortable: true,
        filter: true,
        resizable: true,
      },
      columnTypes: {
        isCancelBeforeStart: {},
      },
      columns: getClientSpecificOrderedColumns(
        defaultColumnOrder,
        columnConfig
      ),
    };
    defaultConfig.columns = defaultConfig.columns.filter(
      (column) => !columnsToHide.includes(column.field)
    );
    return defaultConfig;
  }
}
